import { useCallback, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useOpenDeletePortalEntityDialog } from '@nx-frontend/shared/hooks/use-delete-portal-entity';
import { useNavigateMenuItem } from '@nx-frontend/shared/hooks/use-navigate';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { generatePortalDashboardMenuItem, getMaxIndexFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalUserValues } from '@nx-frontend/shared/store/portal-user-values';
import {
  ChangeEventType,
  DashboardMenuItemTypes,
  getMenuTypeFromString,
  MenuItem,
  MenuItemType,
  MenuType,
  PopupType,
  PORTAL_QUERY_ACTION,
  PortalDashboardMenuItemType,
} from '@nx-frontend/shared/types';

export const useListenPortalQueryActions = () => {
  const [portalUserValues] = useUnit([$portalUserValues]);
  const [openModal, getMaxIndex] = useEvent([openModalEvent, getMaxIndexFx]);
  const params = useSearchParams();
  const [actionParams, setActionParams] = useState<{
    actionType: string;
    actionMenuType: string;
    actionEntityType?: string;
    actionParentId?: string;
    actionItemId?: string;
  } | null>(null);
  const [prevActionParams, setPrevActionParams] = useState<{
    actionType: string;
    actionMenuType: string;
    actionEntityType?: string;
    actionParentId?: string;
    actionItemId?: string;
  } | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const openDeletePortalEntityDialog = useOpenDeletePortalEntityDialog();
  const setChanges = useChanges();
  const navigateMenuItem = useNavigateMenuItem();

  useEffect(() => {
    const actionType = params.get('action-type');
    const actionMenuType = params.get('action-menu-type');
    if (actionType && actionMenuType) {
      const actionParams = {
        actionType,
        actionMenuType,
        actionParentId: params.get('action-parent-id') ?? undefined,
        actionItemId: params.get('action-item-id') ?? undefined,
        actionEntityType: params.get('action-entity-type') ?? undefined,
      };
      setActionParams(actionParams);
    } else {
      setActionParams(null);
    }
  }, [params]);

  useEffect(() => {
    if (actionParams?.toString() !== prevActionParams?.toString()) {
      setPrevActionParams(actionParams);
    }
  }, [actionParams, prevActionParams]);

  const processActions = useCallback(
    ({
      actionType,
      menuType,
      actionEntityType,
      actionParentId,
      actionItemId,
    }: {
      actionType: string;
      menuType: MenuType;
      actionEntityType?: string;
      actionParentId?: string;
      actionItemId?: string;
    }) => {
      const defaultPopupParams = {
        showCloseButton: true,
        closeButtonSize: 18,
      };
      switch (actionType) {
        case PORTAL_QUERY_ACTION.CREATE: {
          const createEntityMenuTypes: string[] = [
            MenuItemType.PORTAL_SECTION,
            MenuItemType.PORTAL_PAGE,
            MenuItemType.PORTAL_PROCESS,
          ];
          if (!actionEntityType) {
            return;
          }
          if (createEntityMenuTypes.includes(actionEntityType)) {
            if (actionEntityType === MenuItemType.PORTAL_PROCESS && !portalUserValues.processViewIntroShowed) {
              openModal({
                content: PopupType.PROCESS_VIEW_INTRO_POPUP,
                modalContainerStyles: '!p-0',
                showCloseButton: false,
                payload: {
                  payloadType: PopupType.PROCESS_VIEW_INTRO_POPUP,
                  parentId: actionParentId,
                  menuType: menuType,
                },
              });
            } else {
              const title =
                actionEntityType === MenuItemType.PORTAL_SECTION
                  ? 'Add Folder'
                  : actionEntityType === MenuItemType.PORTAL_PROCESS
                  ? 'Add Process'
                  : 'Add Page';
              const entityType =
                actionEntityType === MenuItemType.PORTAL_SECTION
                  ? 'folder'
                  : actionEntityType === MenuItemType.PORTAL_PROCESS
                  ? 'process'
                  : 'page';
              openModal({
                ...defaultPopupParams,
                title: title,
                content: PopupType.CREATE_PORTAL_ENTITY,
                payload: {
                  payloadType: PopupType.CREATE_PORTAL_ENTITY,
                  entityType,
                  parentId: actionParentId,
                  menuType: menuType,
                },
              });
            }
          } else if (actionEntityType === MenuItemType.LINK) {
            openModal({
              ...defaultPopupParams,
              title: 'Add External link',
              content: PopupType.CREATE_PORTAL_LINK,
              payload: {
                payloadType: PopupType.CREATE_PORTAL_LINK,
                parentId: actionParentId,
                menuType: menuType,
              },
            });
          } else if (DashboardMenuItemTypes.includes(actionEntityType as MenuItemType)) {
            (async () => {
              const itemType = actionEntityType as PortalDashboardMenuItemType;
              const index = await getMaxIndex({ menuType });
              const menuItem: MenuItem = generatePortalDashboardMenuItem(itemType, {
                parentId: actionParentId,
                index: index ?? 999,
              });
              setChanges({
                type: ChangeEventType.ADD_MENU_ITEM,
                oldValue: undefined,
                newValue: { menuType: menuType, item: menuItem },
              });
              navigateMenuItem({ path: menuItem.url, originalPath: menuItem.originalUrl });
            })();
          }
          break;
        }
        case PORTAL_QUERY_ACTION.DELETE: {
          if (actionItemId) {
            openDeletePortalEntityDialog({ id: actionItemId, menuType });
          }
        }
      }
    },
    [
      getMaxIndex,
      navigateMenuItem,
      openDeletePortalEntityDialog,
      openModal,
      portalUserValues.processViewIntroShowed,
      setChanges,
    ]
  );

  useEffect(() => {
    if (actionParams && actionParams?.toString() !== prevActionParams?.toString()) {
      const menuType = getMenuTypeFromString(actionParams.actionMenuType);
      if (menuType) {
        processActions({
          actionType: actionParams.actionType,
          actionItemId: actionParams.actionItemId,
          actionParentId: actionParams.actionParentId,
          actionEntityType: actionParams.actionEntityType,
          menuType,
        });
      }
    }
  }, [actionParams, prevActionParams, processActions]);

  useEffect(() => {
    if (actionParams) {
      const currentParams = new URLSearchParams(params);
      currentParams.delete('action-type');
      currentParams.delete('action-item-id');
      currentParams.delete('action-entity-type');
      currentParams.delete('action-parent-id');
      currentParams.delete('action-menu-type');
      const preparedUrl = `${pathname}${currentParams.toString() ? '?' : ''}${currentParams.toString()}`;
      router.push(preparedUrl);
    }
  }, [actionParams, params, pathname, router]);

  return async () => {
    return {};
  };
};
